import CaseTaskDetailsDialog from "@/components/CaseTaskDetailsDialog.tsx";
import CompleteTaskForCaseButton from "@/components/CompleteTaskForCaseButton.tsx";
import ConfirmDeleteTaskModal from "@/components/ConfirmDeleteTaskModal.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import type { CaseTaskWithDueDate } from "@/types/caseTask.ts";
import WarningIcon from "@mui/icons-material/Warning";
import { Grid2 as Grid, Stack } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_Row,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";

type Props = {
    data: CaseTaskWithDueDate[];
    showCompleteTaskColumn?: boolean;
};

const MrtCaseTasks = ({ data, showCompleteTaskColumn = true }: Props) => {
    const { hasPermission, employee } = useEmployee();
    const [task, setTask] = useState<CaseTaskWithDueDate | null>(null);
    const [open, setOpen] = useState(false);

    const handleRowClick = (row: MRT_Row<CaseTaskWithDueDate>) => {
        setTask(row.original);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setTask(null);
    };

    const columns = useMemo<MRT_ColumnDef<CaseTaskWithDueDate>[]>(() => {
        const columnDefs: MRT_ColumnDef<CaseTaskWithDueDate>[] = [
            {
                accessorKey: "name",
                header: "Task",
            },
            {
                accessorKey: "metaCreatedWhen",
                header: "Date Assigned",
                Cell: ({ renderedCellValue }) => {
                    if (renderedCellValue instanceof Date) {
                        return renderedCellValue.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });
                    }

                    return "";
                },
            },
            {
                accessorKey: "due",
                header: "Task Due",
                size: 50,
                Cell: ({ row }) => {
                    // If duration is 0, show "N/A" (no due date)
                    if (row.original.duration === 0) {
                        return "N/A";
                    }

                    if (row.original.metaCreatedWhen instanceof Date) {
                        const dueDate = new Date(
                            row.original.metaCreatedWhen.getTime() +
                                row.original.duration * 24 * 60 * 60 * 1000,
                        );

                        return (
                            <Stack
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                }}
                            >
                                {dueDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                })}
                                {dueDate < new Date() && (
                                    <WarningIcon color={"error"} sx={{ ml: 2 }} />
                                )}
                            </Stack>
                        );
                    }

                    return "";
                },
            },
            {
                accessorKey: "dateCompleted",
                header: "Date Completed",
                size: 50,
                Cell: ({ row }) => {
                    if (row.original.dateCompleted instanceof Date) {
                        return (
                            <Stack
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                }}
                            >
                                {row.original.dateCompleted.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                })}
                            </Stack>
                        );
                    }

                    return "";
                },
            },
            {
                accessorKey: "duration",
                header: "Duration",
                size: 50,
            },
            {
                accessorKey: "coreRoles",
                header: "Assigned to Role",
                size: 50,
                Cell: ({ row }) => {
                    if (row.original.coreRoles) {
                        return row.original.coreRoles.join(", ");
                    }

                    return "";
                },
            },
            {
                accessorKey: "assignedBy",
                header: "Assigned By",
                size: 50,
            },
        ];

        if (showCompleteTaskColumn) {
            columnDefs.push({
                accessorKey: "action",
                header: "Complete Task",
                Cell: ({ row }) => {
                    if (row.original.status === "Completed") {
                        return "";
                    }

                    if (
                        hasPermission("complete-for-another:case-task") ||
                        row.original.coreRoles.includes(employee?.dashboardVersion ?? "")
                    ) {
                        return (
                            <div onClick={(e) => e.stopPropagation()}>
                                <CompleteTaskForCaseButton
                                    caseTaskUUID={row.original.idUUID}
                                    caseStatus={"Case Details"}
                                    caseUUID={row.original.case.idUUID}
                                    taskName={row.original.name}
                                    dueDate={row.original.dueDate}
                                    description={row.original.description}
                                    caseTask={row.original}
                                />
                            </div>
                        );
                    }

                    return "";
                },
            });
        }

        if (hasPermission("delete:case-task")) {
            columnDefs.push({
                accessorKey: "deleteAction",
                header: "Delete Task",
                Cell: ({ row }) => {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ConfirmDeleteTaskModal caseTaskUUID={row.original.idUUID} />
                        </div>
                    );
                },
            });
        }

        return columnDefs;
    }, [showCompleteTaskColumn]);

    const table = useMaterialReactTable({
        columns,
        data: data,
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        // top toolbar
        enableTopToolbar: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,

        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,

        enablePagination: false,
        enableBottomToolbar: false,
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<CaseTaskWithDueDate> }) => ({
            onClick: (event) => {
                event.preventDefault();
                handleRowClick(row);
            },
            sx: { cursor: "pointer" },
        }),
    });

    if (data === undefined) {
        return <div>No data</div>;
    }

    return (
        <Grid size={12} key={"completed"} sx={{ mb: 2, width: "100%" }}>
            <MaterialReactTable table={table} />
            <CaseTaskDetailsDialog task={task} open={open} handleClose={handleClose} />
        </Grid>
    );
};

export default MrtCaseTasks;
